import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Check, X, Eye, Users, Money, CreditCard, ArrowsClockwise,
  FileText, Image as ImageIcon, Phone, Envelope, Calendar, UserCircle, Trash, Handshake, Gear, FloppyDisk,
  Megaphone, TrendUp, TrendDown, Scales, Export, Funnel, Target, Play, CursorClick, ChartBar
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [proofLoading, setProofLoading] = useState(false);
  const [affiliateToPay, setAffiliateToPay] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  
  // Video analytics state
  const [videoAnalytics, setVideoAnalytics] = useState(null);
  
  // Marketing config state
  const [marketingConfig, setMarketingConfig] = useState({
    facebook_pixel_id: '',
    tiktok_pixel_id: '',
    google_conversion_id: ''
  });
  const [marketingSaving, setMarketingSaving] = useState(false);
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState('payments');
  const [filteredData, setFilteredData] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  
  // Cross-filters
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'ativo', 'expirado', 'trial'
  const [goalFilter, setGoalFilter] = useState('all'); // 'all', 'perder', 'ganhar', 'manter'

  // Function to get fresh proof URL
  const fetchFreshProofUrl = async (paymentId) => {
    try {
      setProofLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/admin/payment-proof/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.proof_url) {
        setSelectedPayment(prev => ({ ...prev, proof_url: response.data.proof_url }));
        toast.success('URL do comprovativo atualizada');
      }
    } catch (error) {
      console.error('Error fetching proof URL:', error);
      toast.error('Não foi possível obter o comprovativo');
    } finally {
      setProofLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeFilter === 'payments') {
      setFilteredData(pendingPayments);
    }
  }, [activeFilter, pendingPayments]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, paymentsRes, videoRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/pending-payments`),
        axios.get(`${API}/admin/video-analytics`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setPendingPayments(paymentsRes.data.payments || []);
      setFilteredData(paymentsRes.data.payments || []);
      setVideoAnalytics(videoRes.data);
    } catch (error) {
      console.error('Admin data fetch error:', error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = async (filter) => {
    setActiveFilter(filter);
    setFilterLoading(true);
    
    try {
      if (filter === 'users') {
        const res = await axios.get(`${API}/admin/users`);
        const users = res.data.users || [];
        setAllUsers(users);
        applyFilters(users, statusFilter, goalFilter);
      } else if (filter === 'active') {
        const res = await axios.get(`${API}/admin/active-subscriptions`);
        setFilteredData(res.data.users || []);
      } else if (filter === 'payments') {
        setFilteredData(pendingPayments);
      } else if (filter === 'withdrawals') {
        setFilteredData([]);
      } else if (filter === 'affiliates') {
        const res = await axios.get(`${API}/admin/affiliates`);
        setFilteredData(res.data.affiliates || []);
      } else if (filter === 'marketing') {
        await fetchMarketingConfig();
        setFilteredData([]);
      } else if (filter === 'video') {
        // Refresh video analytics
        try {
          const res = await axios.get(`${API}/admin/video-analytics`);
          setVideoAnalytics(res.data);
        } catch (e) {
          console.log("Video analytics not available");
        }
        setFilteredData([]);
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setFilterLoading(false);
    }
  };

  // Apply cross-filters to users list
  const applyFilters = (users, status, goal) => {
    let filtered = [...users];
    
    if (status !== 'all') {
      filtered = filtered.filter(u => u.status === status);
    }
    
    if (goal !== 'all') {
      filtered = filtered.filter(u => u.goal === goal);
    }
    
    setFilteredData(filtered);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    applyFilters(allUsers, status, goalFilter);
  };

  // Handle goal filter change
  const handleGoalFilterChange = (goal) => {
    setGoalFilter(goal);
    applyFilters(allUsers, statusFilter, goal);
  };

  // Export users to CSV
  const handleExportCSV = async () => {
    try {
      toast.info("Preparando exportação...");
      const response = await axios.get(`${API}/admin/users/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nzadifit_utilizadores_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Exportação concluída!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  const handleValidatePayment = async (paymentId, action) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/validate-payment/${paymentId}?action=${action}`);
      toast.success(action === 'approve' ? "Pagamento aprovado!" : "Pagamento rejeitado");
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayAffiliate = async (affiliateId) => {
    setPayLoading(true);
    try {
      const res = await axios.post(`${API}/admin/affiliates/${affiliateId}/pay`);
      toast.success(res.data.message || "Pagamento registado!");
      setAffiliateToPay(null);
      // Refresh affiliates list
      handleFilterClick('affiliates');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar pagamento");
    } finally {
      setPayLoading(false);
    }
  };

  const fetchMarketingConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/marketing`);
      setMarketingConfig({
        facebook_pixel_id: res.data.facebook_pixel_id || '',
        tiktok_pixel_id: res.data.tiktok_pixel_id || '',
        google_conversion_id: res.data.google_conversion_id || ''
      });
    } catch (error) {
      console.error('Error fetching marketing config:', error);
    }
  };

  const handleSaveMarketing = async () => {
    setMarketingSaving(true);
    try {
      await axios.post(`${API}/admin/marketing`, marketingConfig);
      toast.success("Configurações de marketing guardadas!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar configurações");
    } finally {
      setMarketingSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('pt', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('pt', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return `${amount?.toLocaleString('pt')} Kz`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'ativo': 'bg-green-100 text-green-700',
      'inativo': 'bg-red-100 text-red-700',
      'trial': 'bg-blue-100 text-blue-700',
      'expired': 'bg-gray-100 text-gray-700',
      'expirado': 'bg-red-100 text-red-700'
    };
    const labels = {
      'ativo': 'Ativo',
      'inativo': 'Inativo',
      'trial': 'Trial',
      'expired': 'Expirado',
      'expirado': 'Expirado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || badges.trial}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getFilterTitle = () => {
    const titles = {
      'users': 'Todos os Utilizadores',
      'active': 'Assinaturas Ativas',
      'payments': 'Pagamentos Pendentes',
      'withdrawals': 'Saques Pendentes',
      'affiliates': 'Gestão de Afiliados',
      'marketing': 'Configurações de Marketing'
    };
    return titles[activeFilter] || 'Lista';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-primary font-bold text-xl">Nzadi Fit</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <h1 className="text-xl font-bold">Painel Admin</h1>
        </div>
        <button
          onClick={fetchData}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowsClockwise size={24} weight="bold" />
        </button>
      </header>

      <main className="p-4 space-y-4">
        {/* 30-Day Performance Banner */}
        {stats?.last_30_days && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold flex items-center gap-2">
                <TrendUp size={20} weight="bold" />
                Performance (Últimos 30 dias)
              </h3>
              <button
                onClick={handleExportCSV}
                className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full flex items-center gap-1 transition-colors"
                data-testid="export-csv-btn"
              >
                <Export size={14} />
                Exportar CSV
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold font-mono">{stats.last_30_days.total_signups}</p>
                <p className="text-xs text-white/80">Total Cadastros</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold font-mono flex items-center justify-center gap-1">
                  {stats.last_30_days.from_ads}
                  <span className="text-sm">📢</span>
                </p>
                <p className="text-xs text-white/80">Via Anúncios</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold font-mono">{stats.last_30_days.organic}</p>
                <p className="text-xs text-white/80">Orgânico</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats Cards - Now Clickable */}
        <div className="grid grid-cols-2 gap-4">
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={() => handleFilterClick('users')}
            className={`bg-white rounded-2xl p-4 shadow-card text-center transition-all ${
              activeFilter === 'users' ? 'ring-2 ring-primary ring-offset-2' : 'hover:shadow-lg'
            }`}
            data-testid="filter-users"
          >
            <Users size={28} className="text-primary mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono text-foreground">{stats?.total_users || 0}</p>
            <p className="text-xs text-muted-foreground">Utilizadores</p>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            onClick={() => handleFilterClick('active')}
            className={`bg-white rounded-2xl p-4 shadow-card text-center transition-all ${
              activeFilter === 'active' ? 'ring-2 ring-primary ring-offset-2' : 'hover:shadow-lg'
            }`}
            data-testid="filter-active"
          >
            <Check size={28} className="text-primary mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono text-foreground">{stats?.active_subscriptions || 0}</p>
            <p className="text-xs text-muted-foreground">Assinaturas Ativas</p>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            onClick={() => handleFilterClick('payments')}
            className={`rounded-2xl p-4 text-center transition-all ${
              activeFilter === 'payments' 
                ? 'bg-zest/20 ring-2 ring-zest ring-offset-2 border-2 border-zest/30' 
                : 'bg-zest/10 border-2 border-zest/20 hover:bg-zest/20'
            }`}
            data-testid="filter-payments"
          >
            <CreditCard size={28} className="text-zest mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono text-zest">{stats?.pending_payments || 0}</p>
            <p className="text-xs text-muted-foreground">Pagamentos Pendentes</p>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            onClick={() => handleFilterClick('withdrawals')}
            className={`bg-white rounded-2xl p-4 shadow-card text-center transition-all ${
              activeFilter === 'withdrawals' ? 'ring-2 ring-primary ring-offset-2' : 'hover:shadow-lg'
            }`}
            data-testid="filter-withdrawals"
          >
            <Money size={28} className="text-primary mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono text-foreground">{stats?.pending_withdrawals || 0}</p>
            <p className="text-xs text-muted-foreground">Saques Pendentes</p>
          </motion.button>
        </div>

        {/* Affiliates Management Button - Full Width */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          onClick={() => handleFilterClick('affiliates')}
          className={`w-full rounded-2xl p-4 text-center transition-all flex items-center justify-center gap-3 ${
            activeFilter === 'affiliates' 
              ? 'bg-purple-100 ring-2 ring-purple-500 ring-offset-2 border-2 border-purple-300' 
              : 'bg-purple-50 border-2 border-purple-200 hover:bg-purple-100'
          }`}
          data-testid="filter-affiliates"
        >
          <Handshake size={28} className="text-purple-600" weight="duotone" />
          <div className="text-left">
            <p className="font-bold text-purple-700">Gestão de Afiliados</p>
            <p className="text-xs text-purple-500">Comissões e pagamentos</p>
          </div>
        </motion.button>

        {/* Marketing Settings Button - Full Width */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={() => handleFilterClick('marketing')}
          className={`w-full rounded-2xl p-4 text-center transition-all flex items-center justify-center gap-3 ${
            activeFilter === 'marketing' 
              ? 'bg-blue-100 ring-2 ring-blue-500 ring-offset-2 border-2 border-blue-300' 
              : 'bg-blue-50 border-2 border-blue-200 hover:bg-blue-100'
          }`}
          data-testid="filter-marketing"
        >
          <Gear size={28} className="text-blue-600" weight="duotone" />
          <div className="text-left">
            <p className="font-bold text-blue-700">Configurações de Marketing</p>
            <p className="text-xs text-blue-500">Pixels e conversões</p>
          </div>
        </motion.button>

        {/* Video Analytics Button - Full Width */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          onClick={() => handleFilterClick('video')}
          className={`w-full rounded-2xl p-4 text-center transition-all flex items-center justify-center gap-3 ${
            activeFilter === 'video' 
              ? 'bg-purple-100 ring-2 ring-purple-500 ring-offset-2 border-2 border-purple-300' 
              : 'bg-purple-50 border-2 border-purple-200 hover:bg-purple-100'
          }`}
          data-testid="filter-video"
        >
          <Play size={28} className="text-purple-600" weight="duotone" />
          <div className="text-left">
            <p className="font-bold text-purple-700">Analytics de Vídeos</p>
            <p className="text-xs text-purple-500">Views, conclusões e cliques</p>
          </div>
        </motion.button>

        {/* Filtered List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-card overflow-hidden"
        >
          <div className="p-4 bg-muted/30 flex items-center justify-between">
            <h3 className="font-bold text-foreground">{getFilterTitle()}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${
              activeFilter === 'payments' ? 'bg-zest text-white' : 'bg-primary text-white'
            }`}>
              {filteredData.length}
            </span>
          </div>

          {/* Cross-filters for Users */}
          {activeFilter === 'users' && (
            <div className="p-3 bg-muted/10 border-b border-muted space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Funnel size={14} /> Status:
                </span>
                {['all', 'ativo', 'expirado', 'trial'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusFilterChange(status)}
                    className={`text-xs px-3 py-1 rounded-full transition-all ${
                      statusFilter === status 
                        ? 'bg-primary text-white' 
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {status === 'all' ? 'Todos' : status === 'ativo' ? 'Ativo' : status === 'expirado' ? 'Expirado' : 'Trial'}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Target size={14} /> Objetivo:
                </span>
                {['all', 'perder', 'ganhar', 'manter'].map((goal) => (
                  <button
                    key={goal}
                    onClick={() => handleGoalFilterChange(goal)}
                    className={`text-xs px-3 py-1 rounded-full transition-all ${
                      goalFilter === goal 
                        ? 'bg-primary text-white' 
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {goal === 'all' ? 'Todos' : goal === 'perder' ? '🔥 Perder' : goal === 'ganhar' ? '💪 Ganhar' : '⚖️ Manter'}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {filterLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="text-muted-foreground mt-2">Carregando...</p>
            </div>
          ) : activeFilter === 'marketing' ? (
            // Marketing Configuration Form
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                {/* Facebook Pixel */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">f</span>
                      </div>
                      ID do Pixel do Facebook
                    </div>
                  </label>
                  <input
                    type="text"
                    value={marketingConfig.facebook_pixel_id}
                    onChange={(e) => setMarketingConfig({...marketingConfig, facebook_pixel_id: e.target.value})}
                    placeholder="Ex: 123456789012345"
                    className="w-full px-4 py-3 rounded-xl border border-muted bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    data-testid="facebook-pixel-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Cole o ID do pixel do Facebook Ads Manager</p>
                </div>

                {/* TikTok Pixel */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">T</span>
                      </div>
                      ID do Pixel do TikTok
                    </div>
                  </label>
                  <input
                    type="text"
                    value={marketingConfig.tiktok_pixel_id}
                    onChange={(e) => setMarketingConfig({...marketingConfig, tiktok_pixel_id: e.target.value})}
                    placeholder="Ex: C1234567890ABCD"
                    className="w-full px-4 py-3 rounded-xl border border-muted bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    data-testid="tiktok-pixel-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Cole o ID do pixel do TikTok Ads Manager</p>
                </div>

                {/* Google Conversion */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-red-500 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">G</span>
                      </div>
                      ID de Conversão do Google
                    </div>
                  </label>
                  <input
                    type="text"
                    value={marketingConfig.google_conversion_id}
                    onChange={(e) => setMarketingConfig({...marketingConfig, google_conversion_id: e.target.value})}
                    placeholder="Ex: AW-123456789"
                    className="w-full px-4 py-3 rounded-xl border border-muted bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    data-testid="google-conversion-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Cole o ID de conversão do Google Ads</p>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>ℹ️ Como funciona:</strong><br />
                  Os scripts de rastreio serão automaticamente inseridos em todas as páginas. 
                  Os eventos de conversão (<code>CompleteRegistration</code>) disparam automaticamente 
                  quando um utilizador completa o cadastro.
                </p>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveMarketing}
                disabled={marketingSaving}
                className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
                data-testid="save-marketing-btn"
              >
                <FloppyDisk size={20} className="mr-2" />
                {marketingSaving ? 'Guardando...' : 'Guardar Configurações'}
              </Button>
            </div>
          ) : activeFilter === 'video' ? (
            // Video Analytics Dashboard
            <div className="p-6 space-y-6">
              {videoAnalytics?.error ? (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-700">
                    <strong>⚠️ Tabela não encontrada:</strong><br />
                    Cria a tabela <code>video_analytics</code> no Supabase com as colunas:<br />
                    <code>id (uuid), user_id (uuid), event_type (text), video_type (text), user_goal (text), created_at (timestamptz)</code>
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white text-center">
                      <Eye size={24} className="mx-auto mb-1" />
                      <p className="text-2xl font-bold font-mono">{videoAnalytics?.total?.views || 0}</p>
                      <p className="text-xs opacity-80">Visualizações</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white text-center">
                      <Play size={24} className="mx-auto mb-1" weight="fill" />
                      <p className="text-2xl font-bold font-mono">{videoAnalytics?.total?.completed || 0}</p>
                      <p className="text-xs opacity-80">Completos</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white text-center">
                      <CursorClick size={24} className="mx-auto mb-1" />
                      <p className="text-2xl font-bold font-mono">{videoAnalytics?.total?.clicks || 0}</p>
                      <p className="text-xs opacity-80">Cliques</p>
                    </div>
                  </div>

                  {/* Conversion Rate */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-700 font-medium">Taxa de Conversão (Total)</p>
                        <p className="text-3xl font-bold text-amber-600">{videoAnalytics?.total?.conversion_rate || 0}%</p>
                      </div>
                      <ChartBar size={40} className="text-amber-400" weight="duotone" />
                    </div>
                    <p className="text-xs text-amber-600 mt-2">
                      {videoAnalytics?.total?.clicks || 0} cliques de {videoAnalytics?.total?.views || 0} visualizações
                    </p>
                  </div>

                  {/* By Video Type */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-foreground flex items-center gap-2">
                      <Target size={20} className="text-primary" />
                      Por Tipo de Vídeo
                    </h4>
                    
                    {/* Emagrecimento */}
                    <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-bold text-rose-700">🔥 Emagrecimento</p>
                        <span className="text-xs bg-rose-200 text-rose-700 px-2 py-1 rounded-full">
                          {videoAnalytics?.emagrecimento?.conversion_rate || 0}% conversão
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-sm">
                        <div>
                          <p className="font-bold text-rose-600">{videoAnalytics?.emagrecimento?.views || 0}</p>
                          <p className="text-xs text-rose-500">Views</p>
                        </div>
                        <div>
                          <p className="font-bold text-rose-600">{videoAnalytics?.emagrecimento?.completed || 0}</p>
                          <p className="text-xs text-rose-500">Completos</p>
                        </div>
                        <div>
                          <p className="font-bold text-rose-600">{videoAnalytics?.emagrecimento?.clicks || 0}</p>
                          <p className="text-xs text-rose-500">Cliques</p>
                        </div>
                      </div>
                    </div>

                    {/* Ganho de Massa */}
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-bold text-emerald-700">💪 Ganho de Massa</p>
                        <span className="text-xs bg-emerald-200 text-emerald-700 px-2 py-1 rounded-full">
                          {videoAnalytics?.ganhar?.conversion_rate || 0}% conversão
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-sm">
                        <div>
                          <p className="font-bold text-emerald-600">{videoAnalytics?.ganhar?.views || 0}</p>
                          <p className="text-xs text-emerald-500">Views</p>
                        </div>
                        <div>
                          <p className="font-bold text-emerald-600">{videoAnalytics?.ganhar?.completed || 0}</p>
                          <p className="text-xs text-emerald-500">Completos</p>
                        </div>
                        <div>
                          <p className="font-bold text-emerald-600">{videoAnalytics?.ganhar?.clicks || 0}</p>
                          <p className="text-xs text-emerald-500">Cliques</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <p className="text-sm text-purple-700">
                      <strong>📊 O que significa:</strong><br />
                      • <strong>Views:</strong> Quantos abriram o vídeo<br />
                      • <strong>Completos:</strong> Quantos viram até o botão aparecer<br />
                      • <strong>Cliques:</strong> Quantos clicaram no botão
                    </p>
                  </div>
                </>
              )}
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Check size={48} className="mx-auto mb-3 text-primary" weight="duotone" />
              <p>
                {activeFilter === 'payments' && 'Nenhum pagamento pendente!'}
                {activeFilter === 'users' && 'Nenhum utilizador registado.'}
                {activeFilter === 'active' && 'Nenhuma assinatura ativa.'}
                {activeFilter === 'withdrawals' && 'Nenhum saque pendente.'}
                {activeFilter === 'affiliates' && 'Nenhum afiliado com referências.'}
              </p>
            </div>
          ) : activeFilter === 'affiliates' ? (
            // Affiliates list
            <div className="divide-y divide-muted">
              {filteredData.map((affiliate) => (
                <div
                  key={affiliate.id}
                  className="p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Handshake size={24} className="text-purple-600" weight="duotone" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{affiliate.name}</p>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Phone size={14} />
                          <span>{affiliate.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            {affiliate.total_referrals} referências
                          </span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            {affiliate.active_referrals} ativos
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Total vendas: {affiliate.total_sales_formatted}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <div className="text-right">
                        <p className="font-bold text-lg text-purple-700">{affiliate.earnings_formatted}</p>
                        <p className="text-xs text-muted-foreground">Saldo disponível</p>
                      </div>
                      {affiliate.available_balance > 0 && (
                        <button
                          onClick={() => setAffiliateToPay(affiliate)}
                          className="text-sm bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-1"
                          data-testid={`pay-affiliate-${affiliate.id}`}
                        >
                          <Check size={16} weight="bold" />
                          Marcar Pago
                        </button>
                      )}
                      {affiliate.total_paid > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Já pago: {formatAmount(affiliate.total_paid)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : activeFilter === 'payments' ? (
            // Payment list
            <div className="divide-y divide-muted">
              {filteredData.map((payment) => (
                <div
                  key={payment.id}
                  onClick={() => setSelectedPayment(payment)}
                  className="p-4 hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-foreground">{payment.user_name}</p>
                      <p className="text-sm text-muted-foreground">{payment.plan_name} • {formatAmount(payment.amount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{formatDate(payment.created_at)}</p>
                      {payment.proof_url && (
                        <span className="text-xs text-primary">Ver comprovativo</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Users list
            <div className="divide-y divide-muted">
              {filteredData.map((item) => (
                <div
                  key={item.id}
                  className="p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 relative">
                        <UserCircle size={24} className="text-primary" weight="duotone" />
                        {/* Ad indicator */}
                        {item.from_ads && (
                          <span className="absolute -top-1 -right-1 text-sm" title={`Via anúncio: ${item.utm_source}`}>📢</span>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-foreground">{item.name}</p>
                          {item.from_ads && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                              Anúncio
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Phone size={14} />
                          <span>{item.phone}</span>
                        </div>
                        {item.email && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Envelope size={14} />
                            <span>{item.email}</span>
                          </div>
                        )}
                        {/* Goal indicator */}
                        {item.goal && (
                          <div className="mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              item.goal === 'perder' ? 'bg-orange-100 text-orange-700' :
                              item.goal === 'ganhar' ? 'bg-blue-100 text-blue-700' :
                              item.goal === 'manter' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {item.goal === 'perder' ? '🔥 Perder' : item.goal === 'ganhar' ? '💪 Ganhar' : item.goal === 'manter' ? '⚖️ Manter' : '- Sem objetivo'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      {getStatusBadge(item.status)}
                      {item.expiry_date && (
                        <p className="text-xs text-muted-foreground">
                          <Calendar size={12} className="inline mr-1" />
                          Expira: {formatShortDate(item.expiry_date)}
                        </p>
                      )}
                      {(activeFilter === 'users' || activeFilter === 'active') && (
                        <button
                          onClick={() => setUserToDelete(item)}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 mt-1"
                          data-testid={`delete-user-${item.id}`}
                        >
                          <Trash size={14} />
                          Apagar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Payment Detail Modal */}
      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle>Validar Pagamento</DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nome</p>
                  <p className="font-semibold text-primary">{selectedPayment.user_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Telefone</p>
                  <p className="font-mono">{selectedPayment.user_phone}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Email</p>
                  <p className="text-sm">{selectedPayment.user_email || '--'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Plano</p>
                  <p className="font-semibold text-primary">{selectedPayment.plan_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Valor</p>
                  <p className="font-bold text-lg">{formatAmount(selectedPayment.amount)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Data</p>
                  <p className="text-sm">{formatDate(selectedPayment.created_at)}</p>
                </div>
              </div>

              {/* Proof */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Comprovativo:</p>
                {selectedPayment.proof_url ? (
                  selectedPayment.proof_url.startsWith('base64:') && !selectedPayment.proof_url.startsWith('data:') ? (
                    // Old format - just a reference marker
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                      <p className="text-amber-700 text-sm">
                        ⚠️ Comprovativo enviado mas armazenamento externo indisponível.
                      </p>
                      <p className="text-amber-600 text-xs mt-1">
                        Contacte o utilizador em <strong>{selectedPayment.user_phone}</strong> para confirmação.
                      </p>
                    </div>
                  ) : selectedPayment.proof_url.startsWith('data:application/pdf') ? (
                    // Base64 PDF
                    <div className="bg-muted/50 rounded-xl p-4 text-center">
                      <FileText size={48} className="text-primary mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Comprovativo em PDF</p>
                      <a 
                        href={selectedPayment.proof_url} 
                        download={`comprovativo_${selectedPayment.id}.pdf`}
                        className="text-primary text-sm hover:underline mt-2 inline-block"
                      >
                        Baixar PDF
                      </a>
                    </div>
                  ) : selectedPayment.proof_url.startsWith('data:image') ? (
                    // Base64 Image - display directly
                    <img 
                      src={selectedPayment.proof_url} 
                      alt="Comprovativo" 
                      className="w-full rounded-xl max-h-64 object-contain bg-muted/30"
                    />
                  ) : selectedPayment.proof_type === 'pdf' ? (
                    // External PDF URL
                    <a 
                      href={selectedPayment.proof_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-primary hover:underline"
                    >
                      <FileText size={24} />
                      Ver PDF
                    </a>
                  ) : (
                    // External Image URL (Supabase Storage)
                    <div>
                      <img 
                        src={selectedPayment.proof_url} 
                        alt="Comprovativo" 
                        className="w-full rounded-xl max-h-64 object-contain bg-muted/30"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{ display: 'none' }} className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                        <p className="text-amber-700 text-sm mb-2">⚠️ Imagem não carregou</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fetchFreshProofUrl(selectedPayment.id)}
                          disabled={proofLoading}
                        >
                          {proofLoading ? 'A carregar...' : 'Tentar novamente'}
                        </Button>
                        <a 
                          href={selectedPayment.proof_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block text-primary text-sm hover:underline mt-2"
                        >
                          Abrir link direto
                        </a>
                      </div>
                    </div>
                  )
                ) : (
                  <p className="text-muted-foreground italic">Nenhum comprovativo anexado</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-3 mt-4">
            <Button 
              variant="outline"
              onClick={() => handleValidatePayment(selectedPayment?.id, 'reject')}
              disabled={actionLoading}
              className="flex-1 rounded-xl border-destructive text-destructive hover:bg-destructive/10"
            >
              <X size={18} className="mr-2" />
              Rejeitar
            </Button>
            <Button 
              onClick={() => handleValidatePayment(selectedPayment?.id, 'approve')}
              disabled={actionLoading}
              className="flex-1 rounded-xl bg-primary hover:bg-primary/90"
            >
              <Check size={18} className="mr-2" />
              {actionLoading ? "Processando..." : "Aprovar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Modal */}
      <Dialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <DialogContent className="max-w-sm mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-600">Apagar Utilizador</DialogTitle>
          </DialogHeader>
          
          {userToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <p className="text-sm text-red-800">
                  <strong>Atenção!</strong> Esta ação é irreversível. Todos os dados do utilizador serão apagados permanentemente:
                </p>
                <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                  <li>Perfil e dados pessoais</li>
                  <li>Histórico de refeições</li>
                  <li>Registos de peso</li>
                  <li>Pagamentos e comissões</li>
                </ul>
              </div>
              
              <div className="text-center p-3 bg-muted rounded-xl">
                <p className="font-semibold text-foreground">{userToDelete.name}</p>
                <p className="text-sm text-muted-foreground">{userToDelete.phone}</p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-3 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setUserToDelete(null)}
              className="flex-1 rounded-xl"
            >
              Cancelar
            </Button>
            <Button 
              onClick={async () => {
                setDeleteLoading(true);
                try {
                  await axios.delete(`${API}/admin/users/${userToDelete.id}`);
                  toast.success(`Utilizador ${userToDelete.name} apagado`);
                  setUserToDelete(null);
                  // Refresh the list
                  if (activeFilter === 'users' || activeFilter === 'active') {
                    const endpoint = activeFilter === 'users' ? '/admin/users' : '/admin/active-subscriptions';
                    const res = await axios.get(`${API}${endpoint}`);
                    setFilteredData(res.data.users || []);
                  }
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Erro ao apagar utilizador");
                } finally {
                  setDeleteLoading(false);
                }
              }}
              disabled={deleteLoading}
              className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash size={18} className="mr-2" />
              {deleteLoading ? "Apagando..." : "Apagar Permanentemente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Affiliate Confirmation Modal */}
      <Dialog open={!!affiliateToPay} onOpenChange={() => setAffiliateToPay(null)}>
        <DialogContent className="max-w-sm mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-purple-700">Confirmar Pagamento</DialogTitle>
          </DialogHeader>
          
          {affiliateToPay && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <p className="text-sm text-muted-foreground mb-1">Afiliado</p>
                <p className="font-bold text-foreground">{affiliateToPay.name}</p>
                <p className="text-sm text-muted-foreground">{affiliateToPay.phone}</p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-xl border border-green-200 text-center">
                <p className="text-sm text-muted-foreground">Valor a pagar</p>
                <p className="text-3xl font-bold text-green-700">{affiliateToPay.earnings_formatted}</p>
              </div>

              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-700">
                  ⚠️ Ao confirmar, o saldo do afiliado será zerado. Certifique-se de que já fez a transferência antes de marcar como pago.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-3 mt-4">
            <Button 
              variant="outline"
              onClick={() => setAffiliateToPay(null)}
              disabled={payLoading}
              className="flex-1 rounded-xl"
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => handlePayAffiliate(affiliateToPay?.id)}
              disabled={payLoading}
              className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white"
            >
              <Check size={18} className="mr-2" />
              {payLoading ? "Processando..." : "Confirmar Pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
